import React, { useContext, useState } from 'react';
import { ExperiencerService } from 'core/api/services/experiencer';
import { CostService } from 'core/api/services/cost';

const ExperiencerContext = React.createContext(null);

export const ExperiencerProvider = ({ children }) => {
    const [loading, setLoading] = useState(false);
    const [experiencer, setExperiencer] = useState([]);
    const [experiencers, setExperiencers] = useState([]);
    const [total, setTotal] = useState([]);
    const [commissions, setCommissions] = useState([]);
    const [costs, setCosts] = useState([]);
    const [apiKeys, setApiKeys] = useState([]);
    const [loadingSubmit, setLoadingSubmit] = useState(false);

    const getExperiencers = async (options) => {
        try {
            setLoading(true);
            const result = await ExperiencerService.getExperiencers(options);

            if (!result) {
                throw new Error();
            }
            setExperiencers(result.experiencers);
            setTotal(result.total);

            return true;
        } catch (err) {
            console.error(err);
            return false;
        } finally {
            setLoading(false);
        }
    };

    const getExperiencer = async (id) => {
        try {
            setLoading(true);
            const result = await ExperiencerService.get(id);
            if (!result) {
                throw new Error();
            }
            setExperiencer(result);
            return true;
        } catch (err) {
            console.error(err);
            return false;
        } finally {
            setLoading(false);
        }
    };
    const updateExperiencer = async (values) => {
        try {
            setLoading(true);
            const result = await ExperiencerService.update(values);
            if (!result) {
                throw new Error();
            }
            return result;
        } catch (err) {
            console.error(err);
            return false;
        } finally {
            setLoading(false);
        }
    };
    // Commissions
    const getCommissions = async (experiencerId) => {
        try {
            setLoading(true);
            const data = await ExperiencerService.getCommissions(experiencerId);
            if (!data) {
                throw new Error('No Record Found');
            }
            setCommissions(data);
            return true;
        } catch (error) {
            console.error(error);
            return false;
        } finally {
            setLoading(false);
        }
    };

    const patchCommission = async (experiencerId, commissionId, body) => {
        try {
            setLoading(true);
            const data = await ExperiencerService.patchCommission(experiencerId, commissionId, body);
            if (!data) {
                throw new Error('Error Modifying Commission');
            }
            return true;
        } catch (error) {
            console.error(error);
            return false;
        } finally {
            setLoading(false);
        }
    };

    const deleteCommission = async (experiencerId, commissionId) => {
        try {
            setLoading(true);
            const data = await ExperiencerService.deleteCommission(experiencerId, commissionId);

            if (!data) {
                throw new Error('Error Deleting Commission');
            }
        } catch (error) {
            console.error(error);
            return false;
        } finally {
            setLoading(false);
        }
    };

    const addCommission = async (experiencerId, body) => {
        try {
            setLoading(true);
            const data = await ExperiencerService.addCommission(experiencerId, body);
            if (!data) {
                throw new Error('Error Creating Commission');
            }
            return true;
        } catch (error) {
            console.error(error);
            return false;
        } finally {
            setLoading(false);
        }
    };

    // Costs
    const getCosts = async (experiencerId) => {
        try {
            setLoading(true);
            const result = await CostService.getByEntity('experiencer', experiencerId);

            if (!result) {
                throw new Error();
            }

            setCosts(result);

            return true;
        } catch (error) {
            console.error(error);
            setCosts([]);
            return false;
        } finally {
            setLoading(false);
        }
    };

    const postCost = async (values) => {
        try {
            setLoadingSubmit(true);
            const result = await CostService.postCostByEntity('experiencer', values);

            if (!result) {
                throw new Error();
            }

            return result;
        } catch (error) {
            console.error(error);
            return false;
        } finally {
            setLoadingSubmit(false);
        }
    };

    const patchCost = async (costId, values) => {
        try {
            setLoadingSubmit(true);
            const result = await CostService.updateCostByEntity('experiencer', costId, values);

            if (!result) {
                throw new Error();
            }

            return true;
        } catch (error) {
            console.error(error);
            return false;
        } finally {
            setLoadingSubmit(false);
        }
    };

    const deleteCost = async (costId) => {
        try {
            setLoadingSubmit(true);
            const result = await CostService.deleteCostByEntity('experiencer', costId);

            if (!result) {
                throw new Error();
            }

            return true;
        } catch (error) {
            console.error(error);
            return false;
        } finally {
            setLoadingSubmit(false);
        }
    };

    // API Keys
    const getApiKeys = async (experiencerId) => {
        try {
            setLoading(true);
            const data = await ExperiencerService.getApiKeys(experiencerId);
            setApiKeys(data);
            return true;
        } catch (error) {
            console.error('Error fetching API keys:', error);
            setApiKeys([]);
            return false;
        } finally {
            setLoading(false);
        }
    };

    const postApiKey = async (experiencerId, body) => {
        try {
            setLoadingSubmit(true);
            const data = await ExperiencerService.postApiKey(experiencerId, body);
            if (!data) {
                throw new Error('Error Creating API Key');
            }
            return true;
        } catch (error) {
            return false;
        } finally {
            setLoadingSubmit(false);
        }
    };

    const deleteApiKey = async (experiencerId, keyId) => {
        try {
            setLoadingSubmit(true);
            const data = await ExperiencerService.deleteApiKey(experiencerId, keyId);
            if (!data) {
                throw new Error('Error Deleting API Key');
            }
            return true;
        } catch (error) {
            return false;
        } finally {
            setLoadingSubmit(false);
        }
    };

    return (
        <ExperiencerContext.Provider
            value={{
                getExperiencers,
                // update,
                total,
                loading,
                experiencers,
                experiencer,
                setExperiencer,
                getExperiencer,
                updateExperiencer,

                // Commissions
                getCommissions,
                patchCommission,
                deleteCommission,
                addCommission,
                commissions,

                // Costs
                getCosts,
                postCost,
                patchCost,
                deleteCost,
                costs,
                loadingSubmit,

                // API Keys
                getApiKeys,
                postApiKey,
                deleteApiKey,
                apiKeys,
            }}>
            {children}
        </ExperiencerContext.Provider>
    );
};

ExperiencerProvider.displayName = 'ExperiencerProvider';

export const useExperiencer = () => useContext(ExperiencerContext);
